import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import { SectionHeader } from '../ui/SectionHeader';
import { Button } from '../ui/Button';
import { BRAND } from '../../lib/constants';
import { isContactFormConfigured, useContactForm } from '../../hooks/useContactForm';
import styles from './ContactSection.module.css';

export const ContactSection = () => {
  const { t, i18n } = useTranslation();
  const [ctaRef, ctaVisible] = useScrollReveal(0.15);
  const [infoRef, infoVisible] = useScrollReveal(0.15);
  const { values, setField, status, submit } = useContactForm();

  const subject = encodeURIComponent(
    i18n.language === 'it' ? 'Contatto dal sito NexTrainE' : 'Contact from NexTrainE website'
  );
  const mailtoHref = `mailto:${BRAND.email}?subject=${subject}`;
  const isSubmitting = status === 'submitting';

  return (
    <section id="contact" className={styles.section}>
      <div className="container">
        <SectionHeader
          label={t('contact.label')}
          title={t('contact.title')}
          subtitle={t('contact.subtitle')}
        />

        <div className={styles.wrapper}>
          <div className={styles.grid}>
            {/* CTA Card — real form when the API is configured, mailto fallback otherwise */}
            <div
              ref={ctaRef as React.RefObject<HTMLDivElement>}
              className={`${styles.ctaCard} ${ctaVisible ? styles.ctaCardVisible : ''}`}
            >
              {isContactFormConfigured ? (
                <form
                  className={styles.form}
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!isSubmitting) submit();
                  }}
                >
                  <div className={styles.field}>
                    <label htmlFor="contact-name" className={styles.fieldLabel}>
                      {t('contact.form.name_label')}
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      autoComplete="name"
                      placeholder={t('contact.form.name_placeholder')}
                      value={values.name}
                      onChange={(e) => setField('name', e.target.value)}
                      className={styles.input}
                    />
                  </div>

                  <div className={styles.field}>
                    <label htmlFor="contact-email" className={styles.fieldLabel}>
                      {t('contact.form.email_label')}
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      autoComplete="email"
                      placeholder={t('contact.form.email_placeholder')}
                      value={values.email}
                      onChange={(e) => setField('email', e.target.value)}
                      className={styles.input}
                    />
                  </div>

                  <div className={styles.field}>
                    <label htmlFor="contact-message" className={styles.fieldLabel}>
                      {t('contact.form.message_label')}
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      rows={4}
                      placeholder={t('contact.form.message_placeholder')}
                      value={values.message}
                      onChange={(e) => setField('message', e.target.value)}
                      className={styles.textarea}
                    />
                  </div>

                  {/* Honeypot — hidden from sighted and screen-reader users, bots still find and fill it */}
                  <div className={styles.honeypot} aria-hidden="true">
                    <label htmlFor="contact-company">Company</label>
                    <input
                      id="contact-company"
                      name="company"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={values.company}
                      onChange={(e) => setField('company', e.target.value)}
                    />
                  </div>

                  <Button variant="primary" type="submit" disabled={isSubmitting} className={styles.submitButton}>
                    {isSubmitting ? t('contact.form.submitting') : t('contact.form.submit')}
                  </Button>

                  <p role="status" aria-live="polite" className={styles.formStatus}>
                    {status === 'success' && (
                      <span className={styles.formSuccess}>{t('contact.form.success')}</span>
                    )}
                    {status === 'error' && <span className={styles.formError}>{t('contact.form.error')}</span>}
                  </p>

                  <p className={styles.fallbackLine}>
                    {t('contact.form.or_email')}{' '}
                    <a href={mailtoHref} className={styles.fallbackLink}>
                      {t('contact.form.or_email_link', { email: BRAND.email })}
                    </a>
                  </p>
                </form>
              ) : (
                <>
                  <div className={styles.ctaIcon}>
                    <svg width="48" height="48" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <p className={styles.ctaText}>{t('contact.cta_desc')}</p>
                  <Button variant="primary" showArrow href={mailtoHref}>
                    {t('contact.send')}
                  </Button>
                </>
              )}
            </div>

            {/* Info Panel */}
            <div
              ref={infoRef as React.RefObject<HTMLDivElement>}
              className={`${styles.info} ${infoVisible ? styles.infoVisible : ''}`}
            >
              <div className={styles.infoCard}>
                <img src="/logo.svg" alt="" aria-hidden="true" className={styles.infoWatermark} />
                <div className={styles.infoItem}>
                  <div className={styles.infoIcon}>
                    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className={styles.infoLabel}>Email</p>
                    <p className={styles.infoValue}>
                      <a href={`mailto:${BRAND.email}`} className={styles.emailLink}>
                        {BRAND.email}
                      </a>
                    </p>
                  </div>
                </div>

                <div className={styles.infoItem}>
                  <div className={styles.infoIcon}>
                    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className={styles.infoLabel}>{t('contact.address')}</p>
                    <p className={styles.infoValue}>
                      {t('contact.address2')}<br />
                      {t('contact.address3')}<br />
                      {t('contact.city')}
                    </p>
                  </div>
                </div>
              </div>

              <div className={styles.startupBadge}>
                <span className={styles.startupDot} />
                Startup Innovativa • Spin-off {BRAND.university}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
